// What an AI credit actually buys.
//
// Every AI action in the product is metered on the tokens it really consumed
// (backend `src/ai/meter.js`: credits = input x Wi + output x Wo, per model).
// There are no flat prices any more, so nothing here is a tariff — these are
// ESTIMATES from measured jobs, kept in ONE file because roughly a dozen
// surfaces quote a credit equivalent and they used to drift apart.
//
// Measured July 2026 on real AI Writer v2 jobs and editor actions:
//
//   full AI Blogger article    118,000-155,000 credits  (1.2-2.1k words, 6-20 photos)
//   editor AI action              245-939 credits, ~550 average
//   Magic Map                     518-565 credits
//
// Since Aug 2026 each web search the research stage actually runs bills a
// flat 2,000 credits on top (provider fee). Research only runs when the
// draft has [check:] gaps and caps at 8 lookups, so the article range above
// stretches by 0-16,000 — the anchor's headroom already covers the typical
// few-lookup job.
//
// The anchors below round the measurements UP, and the helpers round the
// resulting equivalents DOWN, so every "~N articles" we advertise under-
// promises. That headroom matters most for the article: the drafting contract
// targets 3,500-4,800 words for a things-to-do piece, longer than any job in
// the sample, and a longer article costs more.
//
// If the meter weights change, the model changes, or the pipeline gains a
// stage, re-measure and edit this file. Nothing else in the frontend should
// hardcode a credit equivalent.

export const CREDITS_PER_ARTICLE = 150000;
export const CREDITS_PER_EDITOR_ACTION = 700;
export const CREDITS_PER_MAP = 600;

// Round DOWN to two significant figures, never finer than tens: 71.4 -> 70,
// 1,428 -> 1,400, 28,571 -> 28,000. Rounding down keeps the estimate on the
// safe side of the truth, and the floor of ten stops an approximation being
// quoted to the unit ("~71 edits" claims a precision we do not have).
const roundDownNicely = value => {
  if (value < 10) return Math.floor(value);
  const magnitude = Math.max(10 ** (Math.floor(Math.log10(value)) - 1), 10);
  return Math.floor(value / magnitude) * magnitude;
};

// Articles come in small counts where two significant figures would lie by a
// lot (33 -> 30 is fine, 6.9 -> 6 must not become 0), so they get their own
// rule: exact below twenty, down to the nearest five above it.
export const articlesFor = credits => {
  const exact = credits / CREDITS_PER_ARTICLE;
  if (exact < 20) return Math.floor(exact);
  return Math.floor(exact / 5) * 5;
};

export const editorActionsFor = credits =>
  roundDownNicely(credits / CREDITS_PER_EDITOR_ACTION);

export const mapsFor = credits => roundDownNicely(credits / CREDITS_PER_MAP);

export const formatCount = value => Math.round(value).toLocaleString('en-US');

// Monthly grant per plan. Mirrors `planAiCredits` in the backend's
// src/helpers/stripe.js, which is what the tf-bots cron actually grants.
// Mirrors tf-backend src/helpers/stripe.js planAiCredits (the rule and its
// derivation live there): a plan never costs more AI than half its lowest
// discounted revenue (JP 2026-10-11).
export const MONTHLY_PLAN_CREDITS = {
  starter: 200000,
  basic: 1500000,
  pro: 2500000,
  vip: 5000000,
};

/**
 * The one-line "what this plan's credits are worth" claim, shared by every
 * plan surface so they cannot contradict each other.
 *
 * A plan is sold on what its credits can actually buy in a month: the small
 * plans on editor AI, which they cover comfortably, and Pro and VIP on AI
 * Blogger articles. Quoting articles on a 50k plan would be the mis-sell —
 * one article costs three months of that grant.
 */
export const planCreditsSummary = plan => {
  const credits = MONTHLY_PLAN_CREDITS[plan];
  if (!credits) return '';
  const grant =
    credits >= 1000000 ? `${credits / 1000000}M` : `${credits / 1000}k`;
  // Articles once a month buys at least two: "~1 articles" reads as a
  // mis-sell, and one article would be the whole month's grant.
  const worth =
    credits >= 2 * CREDITS_PER_ARTICLE
      ? `~${articlesFor(credits)} AI Blogger articles`
      : `~${formatCount(editorActionsFor(credits))} AI edits`;
  return `${grant} AI credits/month (${worth})`;
};

// Price per article for a one-time pack, e.g. $10 for 1M -> "1.50".
export const dollarsPerArticle = (credits, dollars) =>
  (dollars / (credits / CREDITS_PER_ARTICLE)).toFixed(2);
